'use client'

import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react'
import * as React from 'react'
import { cn } from '@/lib/utils'
import Image from 'next/image'
import type { CosmicMediaType } from '@/lib/cms/cosmic/types/CosmicProductType'
import { Button } from '../Button'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: 'horizontal' | 'vertical'
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  scrollTo: (index: number) => void
  canScrollPrev: boolean
  canScrollNext: boolean
  selectedIndex: number
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = 'horizontal',
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins,
    )
    const [canScrollPrevious, setCanScrollPrevious] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)
    const [selectedIndex, setSelectedIndex] = React.useState(0)

    const onSelect = React.useCallback((callbackApi: CarouselApi) => {
      if (!callbackApi) {
        return
      }
      setCanScrollPrevious(callbackApi.canScrollPrev())
      setCanScrollNext(callbackApi.canScrollNext())
      setSelectedIndex(callbackApi.selectedScrollSnap())
    }, [])

    const scrollPrevious = React.useCallback(() => {
      api?.scrollPrev()
    }, [api])

    const scrollNext = React.useCallback(() => {
      api?.scrollNext()
    }, [api])

    const scrollTo = React.useCallback(
      (index: number) => {
        api?.scrollTo(index)
      },
      [api],
    )

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          scrollPrevious()
        } else if (event.key === 'ArrowRight') {
          event.preventDefault()
          scrollNext()
        }
      },
      [scrollPrevious, scrollNext],
    )

    React.useEffect(() => {
      if (!api || !setApi) {
        return
      }

      setApi(api)
    }, [api, setApi])

    React.useEffect(() => {
      if (!api) {
        return
      }

      onSelect(api)
      api.on('reInit', onSelect)
      api.on('select', onSelect)

      // eslint-disable-next-line consistent-return
      return () => {
        api?.off('select', onSelect)
      }
    }, [api, onSelect])

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api,
          opts,
          orientation:
            orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev: scrollPrevious,
          scrollNext,
          scrollTo,
          canScrollPrev: canScrollPrevious,
          canScrollNext,
          selectedIndex,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    )
  },
)
Carousel.displayName = 'Carousel'

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div ref={carouselRef} className="overflow-hidden">
      <div
        ref={ref}
        className={cn(
          'flex',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
          className,
        )}
        {...props}
      />
    </div>
  )
})
CarouselContent.displayName = 'CarouselContent'

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useCarousel()

  return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className,
      )}
      {...props}
    />
  )
})
CarouselItem.displayName = 'CarouselItem'

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, variant = 'ghost', size = 'icon', ...props }, ref) => {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel()

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        'absolute  h-8 w-8 rounded-full',
        orientation === 'horizontal'
          ? '-left-12 top-1/2 -translate-y-1/2'
          : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
        className,
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <Image
        height={10}
        width={10}
        alt="arrow"
        src="/icons/arrow.svg"
        className="h-4 w-4 rotate-180"
      />
      <span className="sr-only">Previous slide</span>
    </Button>
  )
})
CarouselPrevious.displayName = 'CarouselPrevious'

const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, variant = 'ghost', size = 'icon', ...props }, ref) => {
  const { orientation, scrollNext, canScrollNext } = useCarousel()

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        'absolute h-8 w-8 rounded-full',
        orientation === 'horizontal'
          ? '-right-12 top-1/2 -translate-y-1/2'
          : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
        className,
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <Image
        height={10}
        width={10}
        alt="arrow"
        src="/icons/arrow.svg"
        className="h-4 w-4"
      />
      <span className="sr-only">Next slide</span>
    </Button>
  )
})
CarouselNext.displayName = 'CarouselNext'

const CarouselThumbs = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    images: CosmicMediaType[]
    indexCallBack?: (index: number) => void
    blurDataUrl: string
  }
>(({ className, ...props }, ref) => {
  const { images, indexCallBack, blurDataUrl } = props

  const {
    scrollTo,
    selectedIndex,
    scrollNext,
    scrollPrev,
    canScrollNext,
    canScrollPrev,
  } = useCarousel()

  React.useEffect(() => {
    setTimeout(() => {
      if (indexCallBack) {
        indexCallBack(0)
      }

      scrollTo(0)
    }, 100)
  }, [images[0]])

  const onThumbClick = (clickedIndex: number) => {
    if (indexCallBack) {
      indexCallBack(clickedIndex)
    }

    scrollTo(clickedIndex)
  }

  return (
    <>
      <div className="flex flex-row gap-2 md:hidden" ref={ref}>
        {images.map((_element, index) => (
          <div
            key={`dot-image-${index}`}
            className={`min-h-2 min-w-2 rounded-full ${
              selectedIndex === index ? 'bg-[#7AA5D4]' : 'bg-[#D9D9D9]'
            }`}
            onClick={() => onThumbClick(index)}
          ></div>
        ))}
      </div>
      <Carousel className="relative hidden w-full md:block">
        <CarouselContent className="flex flex-row justify-center gap-3">
          {images.map((image, index) => (
            <CarouselItem key={index} className="basis-auto">
              <div
                className={`flex max-w-[75px] items-center justify-center rounded-md border
                ${
                  selectedIndex === index
                    ? 'border-solid border-[#5A8FC399]'
                    : 'border-transparent'
                }`}
              >
                <button
                  onClick={() => {
                    onThumbClick(index)
                  }}
                  type="button"
                >
                  <Image
                    className="rounded-md"
                    src={image.url}
                    placeholder="blur"
                    blurDataURL={blurDataUrl}
                    alt={image.altText}
                    height={66}
                    width={75}
                  />
                </button>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div>
          <CarouselPrevious
            onClick={() => scrollPrev()}
            disabled={!canScrollPrev}
            className="left-0"
          />
          <CarouselNext
            onClick={() => scrollNext()}
            disabled={!canScrollNext}
            className="right-0"
          />
        </div>
      </Carousel>
    </>
  )
})
CarouselThumbs.displayName = 'CarouselThumbs'

export {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselThumbs,
}

export { type CarouselApi }
